import {
  defaultDiscount,
  DiscountCodeType,
  PricingPlan,
  TRIAL_WEEK_PLAN_DEFAULT_DISCOUNT,
  type TAgeViewState,
  type TCatFoodTypeBefore,
  type TDiscount,
  type TPricingPlan,
} from "@./suf-store";

import { Theme } from "apps/website/maps/Theme.map";
import type {
  TextTitleVariantsProps,
} from "apps/website/components/base/Text/TextTitle/styles";

import {
  FlowNavigationContext,
  FlowPage,
  PageRequirement,
} from "../../shared/constants";

export const defaultAgeViewState: TAgeViewState = {
  showBirthdaySection: false,
  showNeuteredSection: false,
};

// Sign-up flow store TTL: local store gets cleared after this many days of inactivity
export const SIGNUP_FLOW_TTL_DAYS = 30;

export const trialWeekDefaultDiscountCode = "FIXED_PRICE_SINGLE_10";

export const trialWeekDefaultDiscount = {
  discountCode: TRIAL_WEEK_PLAN_DEFAULT_DISCOUNT.discountCode,
  type: DiscountCodeType.ABSOLUTE_TOTAL,
  value: TRIAL_WEEK_PLAN_DEFAULT_DISCOUNT.value,
};

export const defaultDiscountByTrialPlan: Partial<Record<TPricingPlan, TDiscount>> = {
  [PricingPlan.TRIAL]: defaultDiscount,
  [PricingPlan.TRIAL_WEEK]: trialWeekDefaultDiscount,
};


export const defaultViewState = {
  checkout: {
    // Delivery/Shipping Address Section
    showManualEntryButton: true,
    showAddressSelect: false,
    showShippingAddressFields: false,
    hasClickedFindAddress: false,
    isDeliveryComplete: false,
    hasCompletedDeliveryOnce: false,
    hasSelectedAddressFromLookup: false,
    showShippingAddressWarning: false,
    showBillingAddressFields: false,
    billingSameAsShipping: true,
    showProcessingModal: false,
    isPaymentFormValid: false,
    shouldTriggerPayment: false,
  },
  catParentDetails: {
    isTransitionVisible: false,
  },
  age: {},
};


export const FOOD_TYPES: Record<TCatFoodTypeBefore, TCatFoodTypeBefore> = {
  DRY: "DRY",
  FRESH: "FRESH",
  WET: "WET",
  RAW: "RAW",
  HOME_COOKED: "HOME_COOKED",
};

export enum SignupFlowSlug {
  // Cat pages
  CAT_COUNT = "/sign-up/fresh",
  BASIC_INFORMATION = "/sign-up/fresh/basic-information",
  AGE = "/sign-up/fresh/age",
  EATING_HABITS = "/sign-up/fresh/eating-habits",
  FITNESS = "/sign-up/fresh/fitness",
  WEIGHT = "/sign-up/fresh/weight",
  CURRENT_DIET = "/sign-up/fresh/current-diet",
  MEDICAL_CONDITIONS = "/sign-up/fresh/medical-conditions",
  ALLERGIES = "/sign-up/fresh/allergies",

  // User pages
  MOTIVATION = "/sign-up/fresh/motivation",
  CAT_PARENT_DETAILS = "/sign-up/fresh/cat-parent-details",
  CHECKOUT = "/sign-up/fresh/checkout",
  PRICING = "/sign-up/fresh/pricing",

  // Pages with no validation
  RECIPES = "/sign-up/fresh/recipes",
  WHY_FRESH = "/sign-up/fresh/why-fresh",
  TREATS = "/sign-up/fresh/treats",
}

export interface ISignupFlowPageMetadata {
  title?: string
  titleSize?: TextTitleVariantsProps["size"]
  hideFooter?: boolean
  headerBgColor?: Theme
  bgColor?: Theme
  progressBarBgColor?: Theme
  isUserPage?: boolean
  isTransitionScreen?: boolean
  copyCatAnswers?: boolean
  hidePromoBanner?: boolean
  promoBannerAnimateOnEntry?: boolean
  /**
 * Access requirements are used to determine if a page should be displayed to the user.
 * Example A/B test requirement
 * accessRequirements: [
      (context: FlowNavigationContext) => context.inABTestGroup("new-basic-information-flow"),
    ],
 */
  accessRequirements?: PageRequirement[]
}

const LIGHT_GREY_BG: Theme = "light-grey";

export const SIGNUP_FLOW_PAGES: Record<SignupFlowSlug, ISignupFlowPageMetadata> = {
  [SignupFlowSlug.CAT_COUNT]: {
    promoBannerAnimateOnEntry: true,
    progressBarBgColor: LIGHT_GREY_BG,
  },
  [SignupFlowSlug.BASIC_INFORMATION]: {
    title: "basicInformation",
  },
  [SignupFlowSlug.AGE]: {
    title: "age",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.EATING_HABITS]: {
    title: "eatingHabits",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.FITNESS]: {
    title: "fitness",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.WEIGHT]: {
    title: "weight",
  },
  [SignupFlowSlug.CURRENT_DIET]: {
    title: "currentDiet",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.MEDICAL_CONDITIONS]: {
    title: "medicalConditions",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.ALLERGIES]: {
    title: "allergies",
    copyCatAnswers: true,
  },
  [SignupFlowSlug.MOTIVATION]: {
    title: "motivation",
    isUserPage: true,
  },
  [SignupFlowSlug.CAT_PARENT_DETAILS]: {
    title: "catParentDetails",
    headerBgColor: LIGHT_GREY_BG,
    progressBarBgColor: LIGHT_GREY_BG,
    isUserPage: true,
  },
  [SignupFlowSlug.RECIPES]: {
    title: "recipes",
  },
  [SignupFlowSlug.WHY_FRESH]: {
  },
  [SignupFlowSlug.PRICING]: {
    title: "pricing",
    titleSize: 2,
    isUserPage: true,
    progressBarBgColor: LIGHT_GREY_BG,
  },
  [SignupFlowSlug.TREATS]: {
    title: "treats",
    isUserPage: true,
    progressBarBgColor: LIGHT_GREY_BG,
    accessRequirements: [
      (context: FlowNavigationContext) => !context.hasAnyAllergy([ "CHICKEN", "SALMON", "TUNA" ]),
    ],
  },
  [SignupFlowSlug.CHECKOUT]: {
    title: "checkout",
    isUserPage: true,
    hidePromoBanner: true,
    progressBarBgColor: LIGHT_GREY_BG,
  },
};

export const flowPages: FlowPage<SignupFlowSlug>[] = Object.entries(SIGNUP_FLOW_PAGES).map(
  ([ slug, metadata ]) => ({
    slug: slug as SignupFlowSlug,
    accessRequirements: metadata.accessRequirements ?? [],
    isTransitionScreen: metadata.isTransitionScreen,
  }),
);
